import React from 'react'
import {
  InteriorQuality,
  RealEstateCategory,
  RealEstateFeatures,
  Salutation,
  SegmentationType,
  TransactionType,
  YesNoOrByAppointment,
} from '../../../../../@types/types'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_OFFICE_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import CommercialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/commercial/CommercialCostsRent'
import { propertyOwnerOfficeRentSchema } from './propertyOwnerOfficeRentSchema'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import {
  airConditioningField,
  estateFeatures,
  flooringType,
  interiorQuality,
  lanCablesField,
  parkingSpace,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  buildingType,
  mainFloorSpaceField,
  priceField,
  priceTypeField,
  separableSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'

export const officeRentBasicInfoScreen: FlowScreenElement = {
  screen: SCREENS[SCREEN_BASIC_INFO],
  element: (props) => (
    <BasicInfo
      {...props}
      screenTooltipKey='tooltip.screens.basicInfo.officeRentSell'
      properties={[
        buildingType([
          BuildingTypeOption.LOFT,
          BuildingTypeOption.STUDIO,
          BuildingTypeOption.OFFICE,
          BuildingTypeOption.OFFICE_FLOOR,
          BuildingTypeOption.OFFICE_BUILDING,
          BuildingTypeOption.OFFICE_CENTRE,
          BuildingTypeOption.OFFICE_STORAGE_BUILDING,
          BuildingTypeOption.SURGERY,
          BuildingTypeOption.SURGERY_FLOOR,
          BuildingTypeOption.SURGERY_BUILDING,
          BuildingTypeOption.COMMERCIAL_CENTRE,
          BuildingTypeOption.LIVING_AND_COMMERCIAL_BUILDING,
          BuildingTypeOption.OFFICE_AND_COMMERCIAL_BUILDING,
        ]),
        mainFloorSpaceField(MainFloorType.AREA),
        separableSpaceField(SeparableSpaceType.AREA),
        priceField(PriceFieldType.RENT_PRICE),
        priceTypeField(),
      ]}
    />
  ),
}

export const officeRentOtherFacilities: FlowScreenElement = {
  screen: SCREENS[SCREEN_OTHER_FACILITIES],
  element: (props) => (
    <OtherFacilities
      {...props}
      screenTooltipKey='tooltip.screens.otherFacilities.allRent'
      properties={[
        estateFeatures([
          RealEstateFeatures.BASEMENT,
          RealEstateFeatures.HAS_CANTEEN,
          RealEstateFeatures.HANDICAPPED_ACCESSIBLE,
          RealEstateFeatures.HIGH_VOLTAGE,
          RealEstateFeatures.ELEVATOR,
          RealEstateFeatures.KITCHEN_COMPLETE,
        ]),
        interiorQuality(),
        lanCablesField(),
        airConditioningField(),
        flooringType(),
        totalFloorsField(getDefaultClasses('full')),
        parkingSpace({ showParkingSpaceType: false }),
      ]}
    />
  ),
}

const officeRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  officeRentBasicInfoScreen,
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <CommercialCostsRent
        {...props}
        hasRentDuration={true}
        hasMoveInDate={true}
        screenTooltipKey='tooltip.screens.costs.allRent'
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  officeRentOtherFacilities,
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerOfficeRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.office',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_OFFICE_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_OFFICE_RENT,
    validationSchema: propertyOwnerOfficeRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.OFFICE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQuality.NO_INFORMATION,
    lanCables: YesNoOrByAppointment.NO_INFORMATION,
    airConditioning: YesNoOrByAppointment.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: officeRentFlowScreens,
}
